import { useMemo } from "react";
import useGetTasksBatch from "../../Tasks/hooks/useGetTasksBatch";
import { createErrorState, createLoadingState, createSuccessState, getFirstQueryError, hasAllRequiredData, hasAnyQueryError, isAnyQueryLoading, isDataDefined, } from "../utils/hookStateHelpers";
import { buildTransitionTasksData, extractRootTask, extractTaskIds, } from "../utils/transitionTasksBuilder";
import useGetTransitionDetails from "./useGetTransitionDetails";
const useTransitionTasks = (transitionId, auth) => {
    const transitionQuery = useGetTransitionDetails(transitionId, auth);
    // Fetch root task
    const rootTaskIds = transitionQuery.data?.taskId
        ? [transitionQuery.data.taskId]
        : [];
    const rootTaskQuery = useGetTasksBatch({ taskIds: rootTaskIds }, auth, false);
    // Fetch level 1 tasks (subtasks of root)
    const level1TaskIds = useMemo(() => rootTaskQuery.data?.values[0]?.children?.tasks?.map((t) => t.id) || [], [rootTaskQuery.data]);
    const level1TasksQuery = useGetTasksBatch({ taskIds: level1TaskIds }, auth, false);
    // Fetch level 2 tasks (subtasks of level 1)
    const level2TaskIds = useMemo(() => extractTaskIds(level1TasksQuery.data?.values || []), [level1TasksQuery.data]);
    const level2TasksQuery = useGetTasksBatch({ taskIds: level2TaskIds }, auth, false);
    // Collect all queries for state checking
    const allQueries = [
        transitionQuery,
        rootTaskQuery,
        level1TasksQuery,
        level2TasksQuery,
    ];
    // Return loading state if any query is still loading
    if (isAnyQueryLoading(allQueries)) {
        return createLoadingState();
    }
    // Return error state if any query has an error
    if (hasAnyQueryError(allQueries)) {
        const error = getFirstQueryError(allQueries);
        return createErrorState(error);
    }
    // Return error state if any required data is missing
    if (!hasAllRequiredData(allQueries)) {
        return createErrorState(new Error("Missing required data"));
    }
    // Type guard: At this point we know all data is defined
    if (!isDataDefined(transitionQuery.data) ||
        !isDataDefined(rootTaskQuery.data) ||
        !isDataDefined(level1TasksQuery.data) ||
        !isDataDefined(level2TasksQuery.data)) {
        return createErrorState(new Error("Data validation failed"));
    }
    // Extract and validate root task
    const rootTask = extractRootTask(rootTaskQuery.data);
    if (!rootTask) {
        return createErrorState(new Error("No root task found"));
    }
    // Build and return success state
    try {
        const transitionTasksData = buildTransitionTasksData(transitionQuery.data.taskId, rootTask, level1TasksQuery.data.values, level2TasksQuery.data.values);
        return createSuccessState(transitionTasksData);
    }
    catch (error) {
        console.error("Error processing transition tasks:", error);
        const errorMessage = error instanceof Error ? error : new Error("Unknown processing error");
        return createErrorState(errorMessage);
    }
};
export default useTransitionTasks;
//# sourceMappingURL=useTransitionTasks.js.map